import type { VideoScene } from './videoStudioTypes';

export type StitchTransitionType = 'crossfade' | 'dip_to_black' | 'zoom_dissolve' | 'cut';

export interface StitchOptions {
  transitionType?: StitchTransitionType;
  transitionDurationSeconds?: number;
}

export interface StitchProgress {
  current: number;
  total: number;
  message: string;
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function drawVideoContain(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  width: number,
  height: number,
  scale: number = 1,
  alpha: number = 1
) {
  if (!video.videoWidth || !video.videoHeight) return;
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));

  const vRatio = video.videoWidth / video.videoHeight;
  const cRatio = width / height;
  let dw = width;
  let dh = height;

  if (vRatio > cRatio) {
    dh = width / vRatio;
  } else {
    dw = height * vRatio;
  }

  dw *= scale;
  dh *= scale;
  const dx = (width - dw) / 2;
  const dy = (height - dh) / 2;

  ctx.drawImage(video, dx, dy, dw, dh);
  ctx.restore();
}

export async function stitchVideoScenes(
  scenes: VideoScene[],
  aspectRatio: string = '9:16',
  onProgress?: (info: StitchProgress) => void,
  options?: StitchOptions
): Promise<Blob> {
  const readyScenes = [...scenes]
    .filter((s) => Boolean(s.video_url))
    .sort((a, b) => a.scene_order - b.scene_order);

  if (readyScenes.length < 2) {
    throw new Error('Pelo menos 2 cenas com vídeo pronto são necessárias para juntar em um único vídeo.');
  }

  const transitionType: StitchTransitionType = options?.transitionType || 'crossfade';
  const transitionDur =
    transitionType === 'cut' ? 0.12 : Math.max(0.25, Math.min(1.0, options?.transitionDurationSeconds ?? 0.5));

  let width = 720;
  let height = 1280;
  if (aspectRatio === '1:1') {
    width = 720;
    height = 720;
  } else if (aspectRatio === '16:9') {
    width = 1280;
    height = 720;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Falha ao inicializar o renderizador de vídeo Canvas.');

  // Canvas auxiliar para capturar o último frame da cena anterior durante a transição
  const prevCanvas = document.createElement('canvas');
  prevCanvas.width = width;
  prevCanvas.height = height;
  const prevCtx = prevCanvas.getContext('2d');

  const canvasStream = canvas.captureStream(30);

  const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
  let audioCtx: AudioContext | null = null;
  let audioDest: MediaStreamAudioDestinationNode | null = null;
  if (AudioContextClass) {
    try {
      audioCtx = new AudioContextClass();
      audioDest = audioCtx.createMediaStreamDestination();
    } catch (_) {}
  }

  const tracks: MediaStreamTrack[] = [...canvasStream.getVideoTracks()];
  if (audioDest) {
    tracks.push(...audioDest.stream.getAudioTracks());
  }
  const combinedStream = new MediaStream(tracks);

  let selectedMimeType = 'video/mp4';
  if (MediaRecorder.isTypeSupported('video/mp4;codecs=avc1,mp4a.40.2')) {
    selectedMimeType = 'video/mp4;codecs=avc1,mp4a.40.2';
  } else if (MediaRecorder.isTypeSupported('video/mp4')) {
    selectedMimeType = 'video/mp4';
  } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
    selectedMimeType = 'video/webm;codecs=vp9,opus';
  } else {
    selectedMimeType = 'video/webm';
  }

  const chunks: Blob[] = [];
  const recorder = new MediaRecorder(combinedStream, {
    mimeType: selectedMimeType,
    videoBitsPerSecond: 5_000_000,
  });

  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };

  let animFrameId: number | null = null;
  let hasPreviousFrameSnapshot = false;

  try {
    for (let i = 0; i < readyScenes.length; i++) {
      const scene = readyScenes[i];
      const isFirst = i === 0;
      const isLast = i === readyScenes.length - 1;

      if (onProgress) {
        onProgress({
          current: i + 1,
          total: readyScenes.length,
          message: `Unindo Cena #${scene.scene_order} com transição suave (${i + 1} de ${readyScenes.length})...`,
        });
      }

      await new Promise<void>((resolve, reject) => {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.src = scene.video_url!;
        video.muted = false;
        video.playsInline = true;
        video.preload = 'auto';

        let gainNode: GainNode | null = null;

        const cleanup = () => {
          if (animFrameId) {
            cancelAnimationFrame(animFrameId);
            animFrameId = null;
          }
          video.pause();
          video.removeAttribute('src');
          video.load();
        };

        video.onloadedmetadata = async () => {
          const clipDuration = Number.isFinite(video.duration) && video.duration > 0
            ? video.duration
            : (Number(scene.duration_seconds) || 5);

          if (audioCtx && audioDest) {
            try {
              if (audioCtx.state === 'suspended') await audioCtx.resume();
              const audioSource = audioCtx.createMediaElementSource(video);
              gainNode = audioCtx.createGain();
              // Envelope suave de entrada para evitar estalos secos entre takes
              gainNode.gain.setValueAtTime(isFirst ? 1.0 : 0.05, audioCtx.currentTime);
              if (!isFirst) {
                gainNode.gain.linearRampToValueAtTime(1.0, audioCtx.currentTime + Math.min(0.28, transitionDur));
              }
              audioSource.connect(gainNode);
              gainNode.connect(audioDest);
            } catch (_) {}
          }

          let fadeOutScheduled = false;

          const renderFrame = () => {
            if (!video.paused && !video.ended) {
              const t = video.currentTime || 0;
              const remaining = Math.max(0, clipDuration - t);

              // Agenda fade-out de áudio nos últimos instantes da cena para evitar corte abrupto de voz/ambiência
              if (!isLast && !fadeOutScheduled && remaining <= 0.28 && gainNode && audioCtx) {
                fadeOutScheduled = true;
                try {
                  gainNode.gain.setValueAtTime(gainNode.gain.value, audioCtx.currentTime);
                  gainNode.gain.linearRampToValueAtTime(0.08, audioCtx.currentTime + Math.max(0.12, remaining));
                } catch (_) {}
              }

              ctx.fillStyle = '#090d16';
              ctx.fillRect(0, 0, width, height);

              // Transição de entrada sobre o último frame da cena anterior
              if (!isFirst && hasPreviousFrameSnapshot && t < transitionDur && transitionType !== 'cut') {
                const progress = easeInOutCubic(Math.min(1, t / transitionDur));

                if (transitionType === 'crossfade') {
                  ctx.save();
                  ctx.globalAlpha = 1 - progress;
                  ctx.drawImage(prevCanvas, 0, 0, width, height);
                  ctx.restore();
                  drawVideoContain(ctx, video, width, height, 1.0, progress);
                } else if (transitionType === 'zoom_dissolve') {
                  ctx.save();
                  ctx.globalAlpha = 1 - progress;
                  const outScale = 1 + 0.05 * progress;
                  const sw = width * outScale;
                  const sh = height * outScale;
                  ctx.drawImage(prevCanvas, (width - sw) / 2, (height - sh) / 2, sw, sh);
                  ctx.restore();
                  const inScale = 1.04 - 0.04 * progress;
                  drawVideoContain(ctx, video, width, height, inScale, progress);
                } else if (transitionType === 'dip_to_black') {
                  if (progress < 0.5) {
                    const p = progress * 2;
                    ctx.save();
                    ctx.globalAlpha = 1 - p;
                    ctx.drawImage(prevCanvas, 0, 0, width, height);
                    ctx.restore();
                  } else {
                    const p = (progress - 0.5) * 2;
                    drawVideoContain(ctx, video, width, height, 1.0, p);
                  }
                }
              } else {
                drawVideoContain(ctx, video, width, height, 1.0, 1.0);
              }

              // Atualiza snapshot do frame atual para servir de ponte suave para a próxima cena
              if (prevCtx && remaining < transitionDur + 0.25) {
                prevCtx.fillStyle = '#090d16';
                prevCtx.fillRect(0, 0, width, height);
                drawVideoContain(prevCtx, video, width, height, 1.0, 1.0);
                hasPreviousFrameSnapshot = true;
              }

              animFrameId = requestAnimationFrame(renderFrame);
            }
          };

          video.onplaying = () => {
            if (recorder.state === 'inactive') {
              recorder.start(100);
            } else if (recorder.state === 'paused') {
              recorder.resume();
            }
            if (!animFrameId) {
              animFrameId = requestAnimationFrame(renderFrame);
            }
          };

          video.play().catch((err) => {
            cleanup();
            reject(err);
          });
        };

        video.onended = () => {
          // Garante captura do último frame antes de encerrar a cena
          if (prevCtx && video.videoWidth > 0) {
            prevCtx.fillStyle = '#090d16';
            prevCtx.fillRect(0, 0, width, height);
            drawVideoContain(prevCtx, video, width, height, 1.0, 1.0);
            hasPreviousFrameSnapshot = true;
          }
          cleanup();
          resolve();
        };

        video.onerror = () => {
          cleanup();
          reject(new Error(`Falha ao reproduzir o vídeo da Cena #${scene.scene_order}`));
        };
      });
    }

    await new Promise((r) => setTimeout(r, 180));

    return await new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        const fullBlob = new Blob(chunks, { type: selectedMimeType });
        resolve(fullBlob);
      };
      if (recorder.state !== 'inactive') {
        recorder.stop();
      } else {
        resolve(new Blob(chunks, { type: selectedMimeType }));
      }
      if (audioCtx) audioCtx.close().catch(() => {});
    });
  } catch (err) {
    if (recorder.state !== 'inactive') recorder.stop();
    if (audioCtx) audioCtx.close().catch(() => {});
    throw err;
  }
}
